import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Eye, Check, FileText, Scale, Download } from 'lucide-react';
import { settlementsApi } from '../../lib/api';
import PageHeader from '../../components/PageHeader';
import DataTable from '../../components/DataTable';
import Modal from '../../components/Modal';

export default function SettlementList() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState<'pending' | 'all'>('pending');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [createForm, setCreateForm] = useState({
    processingDate: new Date().toISOString().split('T')[0],
  });
  const [confirmForm, setConfirmForm] = useState({
    confirmedWeight: '',
    sellingUnitPrice: '',
    weightDifference: '',
    remarks: '',
  });

  // メイン日付フィルタ
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['settlements', tab, search, dateFrom, dateTo],
    queryFn: () => tab === 'pending' 
      ? settlementsApi.getPending({ search })
      : settlementsApi.getAll({ search, dateFrom, dateTo }),
    enabled: tab === 'pending' || Boolean(dateFrom && dateTo),
  });

  const createMutation = useMutation({
    mutationFn: settlementsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settlements'] });
      toast.success('精算を起票しました');
      setIsCreateModalOpen(false);
      setSelectedItem(null);
    },
    onError: () => toast.error('精算起票に失敗しました'),
  });

  const confirmMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => settlementsApi.confirm(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settlements'] });
      toast.success('重量を確定しました');
      setIsConfirmModalOpen(false);
      setSelectedItem(null);
    },
    onError: () => toast.error('重量確定に失敗しました'),
  });

  const settleMutation = useMutation({
    mutationFn: settlementsApi.settle,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settlements'] });
      toast.success('精算を完了しました');
    },
    onError: () => toast.error('精算完了に失敗しました'),
  });

  const handleCreateSettlement = (row: any) => {
    setSelectedItem(row);
    setIsCreateModalOpen(true);
  };

  const handleSubmitCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedItem) return;

    createMutation.mutate({
      shipmentId: selectedItem.shipment.id,
      processingDate: createForm.processingDate,
      estimatedWeight: selectedItem.shipment.weight,
    });
  };

  const handleConfirmWeight = (row: any) => {
    setSelectedItem(row);
    setConfirmForm({
      confirmedWeight: row.settlement?.estimatedWeight?.toString() || '',
      sellingUnitPrice: row.shipment?.unitPrice?.toString() || '',
      weightDifference: '0',
      remarks: '',
    });
    setIsConfirmModalOpen(true);
  };

  const handleSubmitConfirm = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedItem) return;

    confirmMutation.mutate({
      id: selectedItem.settlement.id,
      data: {
        confirmedWeight: confirmForm.confirmedWeight,
        sellingUnitPrice: confirmForm.sellingUnitPrice,
        weightDifference: confirmForm.weightDifference,
        remarks: confirmForm.remarks,
      },
    });
  };

  const handleSettle = (row: any) => {
    if (window.confirm('精算を完了しますか？')) {
      settleMutation.mutate(row.settlement.id);
    }
  };

  const handleDetail = (row: any) => {
    setSelectedItem(row);
    setIsDetailModalOpen(true);
  };

  const handleExportPending = () => {
    const items = data?.data?.data || [];
    if (items.length === 0) {
      toast.error('エクスポートするデータがありません');
      return;
    }

    const headers = ['LOT', '社内LOT', '出庫日', '仕入先', '商品名', 'ブランド', '数量', '概算重量', '冷蔵庫', '売り先'];
    const rows = items.map((item: any) => [
      item.shipment?.shipmentLot || '',
      item.purchase?.internalLot || '',
      item.shipment?.shipmentDate || '',
      item.supplier?.name || '',
      item.purchase?.productName || '',
      item.brand?.name || '',
      item.shipment?.quantity || '',
      item.shipment?.weight || '',
      item.coldStorage?.name || '',
      item.customer?.name || '',
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `出切り起票前_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('CSVをエクスポートしました');
  };

  const handleExportAll = () => {
    const items = data?.data?.data || [];
    if (items.length === 0) {
      toast.error('エクスポートするデータがありません');
      return;
    }

    const headers = ['精算LOT', '処理日', '売り先', '商品名', 'ブランド', '概算(kg)', '確定(kg)', '差異', '売単価', '冷蔵庫', '状態'];
    const rows = items.map((item: any) => [
      item.settlement?.settlementLot || '',
      item.settlement?.processingDate || '',
      item.customer?.name || '',
      item.purchase?.productName || '',
      item.brand?.name || '',
      item.settlement?.estimatedWeight || '',
      item.settlement?.confirmedWeight || '',
      item.settlement?.weightDifference || '',
      item.settlement?.sellingUnitPrice || '',
      item.coldStorage?.name || '',
      item.settlement?.status === 'settled' ? '精算済' :
      item.settlement?.status === 'awaiting_confirmation' ? '確定待' : '未処理',
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `精算一覧_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('CSVをエクスポートしました');
  };

  const formatNumber = (num: any) => {
    if (num === null || num === undefined) return '-';
    return Number(num).toLocaleString('ja-JP');
  };

  const pendingColumns = [
    { 
      key: 'shipment.shipmentLot', 
      label: 'LOT', 
      width: '80px',
      render: (row: any) => row.shipment?.shipmentLot || '-',
    },
    { 
      key: 'purchase.internalLot', 
      label: '社内LOT',
      width: '100px',
      render: (row: any) => row.purchase?.internalLot || '-',
    },
    { 
      key: 'shipment.shipmentDate', 
      label: '出庫日', 
      width: '100px',
      render: (row: any) => row.shipment?.shipmentDate || '-',
    },
    { 
      key: 'supplier.name', 
      label: '仕入先',
      render: (row: any) => row.supplier?.name || '-',
    },
    { 
      key: 'purchase.productName', 
      label: '商品名',
      render: (row: any) => row.purchase?.productName || '-',
    },
    { 
      key: 'brand.name', 
      label: 'ブランド',
      render: (row: any) => row.brand?.name || '-',
    },
    { 
      key: 'shipment.quantity', 
      label: '数量',
      width: '70px',
      render: (row: any) => formatNumber(row.shipment?.quantity),
    },
    { 
      key: 'shipment.weight', 
      label: '概算重量',
      width: '100px',
      render: (row: any) => formatNumber(row.shipment?.weight),
    },
    { 
      key: 'coldStorage.name', 
      label: '冷蔵庫',
      render: (row: any) => row.coldStorage?.name || '-',
    },
    { 
      key: 'customer.name', 
      label: '売り先',
      render: (row: any) => row.customer?.name || '-',
    },
    {
      key: 'actions',
      label: '操作',
      width: '100px',
      render: (row: any) => (
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); handleCreateSettlement(row); }}
            className="p-1.5 rounded-lg transition-colors hover:bg-gray-100"
            style={{ color: 'var(--success-color)' }}
            title="精算起票"
          >
            <Check size={16} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleDetail(row); }}
            className="p-1.5 rounded-lg transition-colors hover:bg-gray-100"
            style={{ color: 'var(--accent-color)' }}
            title="詳細"
          >
            <Eye size={16} />
          </button>
        </div>
      ),
    },
  ];

  const allColumns = [
    { 
      key: 'settlement.settlementLot', 
      label: '精算LOT', 
      width: '100px',
      render: (row: any) => row.settlement?.settlementLot || '-',
    },
    { 
      key: 'settlement.processingDate', 
      label: '処理日', 
      width: '100px',
      render: (row: any) => row.settlement?.processingDate || '-',
    },
    { 
      key: 'customer.name', 
      label: '売り先',
      render: (row: any) => row.customer?.name || '-',
    },
    { 
      key: 'purchase.productName', 
      label: '商品名',
      render: (row: any) => row.purchase?.productName || '-',
    },
    { 
      key: 'brand.name', 
      label: 'ブランド',
      render: (row: any) => row.brand?.name || '-',
    },
    { 
      key: 'settlement.estimatedWeight', 
      label: '概算(kg)',
      width: '90px',
      render: (row: any) => formatNumber(row.settlement?.estimatedWeight),
    },
    { 
      key: 'settlement.confirmedWeight', 
      label: '確定(kg)',
      width: '90px',
      render: (row: any) => formatNumber(row.settlement?.confirmedWeight),
    },
    { 
      key: 'settlement.weightDifference', 
      label: '差異',
      width: '80px',
      render: (row: any) => {
        const diff = Number(row.settlement?.weightDifference) || 0;
        return (
          <span style={{ color: diff > 0 ? 'var(--success-color)' : diff < 0 ? 'var(--error-color)' : 'inherit' }}>
            {diff > 0 ? '+' : ''}{formatNumber(diff)}
          </span>
        );
      },
    },
    { 
      key: 'settlement.sellingUnitPrice', 
      label: '売単価',
      width: '90px',
      render: (row: any) => `¥${formatNumber(row.settlement?.sellingUnitPrice)}`,
    },
    { 
      key: 'coldStorage.name', 
      label: '冷蔵庫',
      render: (row: any) => row.coldStorage?.name || '-',
    },
    {
      key: 'settlement.status',
      label: '状態',
      width: '90px',
      render: (row: any) => {
        const status = row.settlement?.status;
        return (
          <span className={
            status === 'settled' ? 'badge badge-success' :
            status === 'awaiting_confirmation' ? 'badge badge-warning' :
            'badge badge-error'
          }>
            {status === 'settled' ? '精算済' :
             status === 'awaiting_confirmation' ? '確定待' : '未処理'}
          </span>
        );
      },
    },
    {
      key: 'actions',
      label: '操作',
      width: '120px',
      render: (row: any) => (
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); handleDetail(row); }}
            className="p-1.5 rounded-lg transition-colors hover:bg-gray-100"
            style={{ color: 'var(--accent-color)' }}
            title="詳細"
          >
            <Eye size={16} />
          </button>
          {row.settlement?.status === 'awaiting_confirmation' && (
            <button
              onClick={(e) => { e.stopPropagation(); handleConfirmWeight(row); }}
              className="p-1.5 rounded-lg transition-colors hover:bg-gray-100"
              style={{ color: 'var(--warning-color)' }}
              title="重量確定"
            >
              <Scale size={16} />
            </button>
          )}
          {row.settlement?.status === 'awaiting_confirmation' && row.settlement?.confirmedWeight && (
            <button
              onClick={(e) => { e.stopPropagation(); handleSettle(row); }}
              className="p-1.5 rounded-lg transition-colors hover:bg-gray-100"
              style={{ color: 'var(--success-color)' }}
              title="精算完了"
            >
              <Check size={16} />
            </button>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); toast('精算依頼書PDFを生成します'); }}
            className="p-1.5 rounded-lg transition-colors hover:bg-gray-100"
            style={{ color: 'var(--text-secondary)' }}
            title="精算依頼書"
          >
            <FileText size={16} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="animate-fadeIn">
      <PageHeader
        title="精算管理"
        breadcrumbs={[{ label: '精算管理' }]}
        dateFilterKey="settlements-date-filter"
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateChange={(from, to) => { setDateFrom(from); setDateTo(to); }}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="商品名で検索..."
        onApply={() => refetch()}
        actions={
          <button className="btn btn-outline btn-sm" onClick={tab === 'pending' ? handleExportPending : handleExportAll}>
            <Download size={14} />
          </button>
        }
      />

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            tab === 'pending' 
              ? 'bg-blue-600 text-white' 
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
          onClick={() => setTab('pending')}
        >
          出切り起票前
        </button>
        <button
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            tab === 'all' 
              ? 'bg-blue-600 text-white' 
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
          onClick={() => setTab('all')}
        >
          精算一覧
        </button>
      </div>

      <DataTable
        columns={tab === 'pending' ? pendingColumns : allColumns}
        data={data?.data?.data || []}
        loading={isLoading}
        emptyMessage={tab === 'pending' ? '精算待ちの出庫がありません' : '精算データがありません'}
        defaultSortKey={tab === 'pending' ? 'shipment.shipmentDate' : 'settlement.processingDate'}
        defaultSortOrder="desc"
      />

      {/* Create Settlement Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => { setIsCreateModalOpen(false); setSelectedItem(null); }}
        title="精算起票"
        size="md"
      >
        {selectedItem && (
          <form onSubmit={handleSubmitCreate} className="space-y-4">
            <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--light-gray-tertiary)' }}>
              <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                {selectedItem.purchase?.productName}
              </p>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                LOT: {selectedItem.shipment?.shipmentLot} | 
                出庫日: {selectedItem.shipment?.shipmentDate} |
                売り先: {selectedItem.customer?.name}
              </p>
              <p className="text-sm mt-2" style={{ color: 'var(--text-secondary)' }}>
                数量: {formatNumber(selectedItem.shipment?.quantity)}ケース |
                概算重量: {formatNumber(selectedItem.shipment?.weight)}kg
              </p>
            </div>

            <div>
              <label className="label">処理日 <span className="text-red-500">*</span></label>
              <input
                type="date"
                className="input-field"
                value={createForm.processingDate}
                onChange={(e) => setCreateForm({ ...createForm, processingDate: e.target.value })}
                required
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => { setIsCreateModalOpen(false); setSelectedItem(null); }}
              >
                キャンセル
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={createMutation.isPending}
              >
                起票する
              </button>
            </div>
          </form>
        )}
      </Modal>

      {/* Confirm Weight Modal */}
      <Modal
        isOpen={isConfirmModalOpen}
        onClose={() => { setIsConfirmModalOpen(false); setSelectedItem(null); }}
        title="重量確定"
        size="md"
      >
        {selectedItem && (
          <form onSubmit={handleSubmitConfirm} className="space-y-4">
            <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--light-gray-tertiary)' }}>
              <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                {selectedItem.purchase?.productName}
              </p>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                精算LOT: {selectedItem.settlement?.settlementLot} |
                概算重量: {formatNumber(selectedItem.settlement?.estimatedWeight)}kg
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">確定重量(kg) <span className="text-red-500">*</span></label>
                <input
                  type="number"
                  step="0.01"
                  className="input-field"
                  value={confirmForm.confirmedWeight}
                  onChange={(e) => {
                    const confirmed = parseFloat(e.target.value) || 0;
                    const estimated = parseFloat(selectedItem.settlement?.estimatedWeight) || 0;
                    setConfirmForm({
                      ...confirmForm,
                      confirmedWeight: e.target.value,
                      weightDifference: (confirmed - estimated).toFixed(2),
                    });
                  }}
                  required
                />
              </div>
              <div>
                <label className="label">重量差異(kg)</label>
                <input
                  type="number"
                  step="0.01"
                  className="input-field"
                  value={confirmForm.weightDifference}
                  disabled
                  style={{ backgroundColor: 'var(--light-gray-secondary)' }}
                />
              </div>
            </div>

            <div>
              <label className="label">売単価(円/kg) <span className="text-red-500">*</span></label>
              <input
                type="number"
                step="0.01"
                className="input-field"
                value={confirmForm.sellingUnitPrice}
                onChange={(e) => setConfirmForm({ ...confirmForm, sellingUnitPrice: e.target.value })}
                required
              />
            </div>

            <div>
              <label className="label">備考</label>
              <textarea
                className="input-field"
                rows={2}
                value={confirmForm.remarks}
                onChange={(e) => setConfirmForm({ ...confirmForm, remarks: e.target.value })}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => { setIsConfirmModalOpen(false); setSelectedItem(null); }}
              >
                キャンセル
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={confirmMutation.isPending}
              >
                重量確定
              </button>
            </div>
          </form>
        )}
      </Modal>

      {/* Detail Modal */}
      <Modal
        isOpen={isDetailModalOpen}
        onClose={() => { setIsDetailModalOpen(false); setSelectedItem(null); }}
        title="詳細"
        size="lg"
      >
        {selectedItem && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>商品名</p>
                <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                  {selectedItem.purchase?.productName}
                </p>
              </div>
              <div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>ブランド</p>
                <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                  {selectedItem.brand?.name || '-'}
                </p>
              </div>
              <div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>仕入先</p>
                <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                  {selectedItem.supplier?.name || '-'}
                </p>
              </div>
              <div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>売り先</p>
                <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                  {selectedItem.customer?.name}
                </p>
              </div>
              <div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>冷蔵庫</p>
                <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                  {selectedItem.coldStorage?.name || '-'}
                </p>
              </div>
              <div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>出庫日</p>
                <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                  {selectedItem.shipment?.shipmentDate}
                </p>
              </div>
            </div>

            <div className="border-t pt-4" style={{ borderColor: 'var(--border-color)' }}>
              <h4 className="font-medium mb-3" style={{ color: 'var(--text-primary)' }}>出庫情報</h4>
              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--light-gray-tertiary)' }}>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>LOT</p>
                  <p className="font-bold" style={{ color: 'var(--text-primary)' }}>
                    {selectedItem.shipment?.shipmentLot}
                  </p>
                </div>
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--light-gray-tertiary)' }}>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>数量</p>
                  <p className="font-bold" style={{ color: 'var(--text-primary)' }}>
                    {formatNumber(selectedItem.shipment?.quantity)} ケース
                  </p>
                </div>
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--light-gray-tertiary)' }}>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>概算重量</p>
                  <p className="font-bold" style={{ color: 'var(--text-primary)' }}>
                    {formatNumber(selectedItem.shipment?.weight)} kg
                  </p>
                </div>
              </div>
            </div>

            {selectedItem.settlement && (
              <div className="border-t pt-4" style={{ borderColor: 'var(--border-color)' }}>
                <h4 className="font-medium mb-3" style={{ color: 'var(--text-primary)' }}>精算情報</h4>
                <div className="grid grid-cols-4 gap-4">
                  <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--light-gray-tertiary)' }}>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>精算LOT</p>
                    <p className="font-bold" style={{ color: 'var(--text-primary)' }}>
                      {selectedItem.settlement?.settlementLot}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--light-gray-tertiary)' }}>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>確定重量</p>
                    <p className="font-bold" style={{ color: 'var(--text-primary)' }}>
                      {formatNumber(selectedItem.settlement?.confirmedWeight) || '-'} kg
                    </p>
                  </div>
                  <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--light-gray-tertiary)' }}>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>売単価</p>
                    <p className="font-bold" style={{ color: 'var(--text-primary)' }}>
                      ¥{formatNumber(selectedItem.settlement?.sellingUnitPrice)}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--light-gray-tertiary)' }}>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>状態</p>
                    <p className="font-bold" style={{ color: 'var(--text-primary)' }}>
                      {selectedItem.settlement?.status === 'settled' ? '精算済' :
                       selectedItem.settlement?.status === 'awaiting_confirmation' ? '確定待' : '未処理'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
              <button
                className="btn btn-outline"
                onClick={() => { setIsDetailModalOpen(false); setSelectedItem(null); }}
              >
                閉じる
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
